// src/pages/AddItemPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createItem, getCategories, getLocations } from '../services/api';
import LabelPrintModal from '../components/LabelPrintModal';
import PhotoUpload from '../components/PhotoUpload';

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

export default function AddItemPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedItem, setSavedItem] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [form, setForm] = useState({
    item_type: 'asset', name: '', category_id: '', manufacturer_id: '',
    model_number: '', serial_number: '', purchase_price: '',
    purchase_date: '', condition: '',
    battery_platform: '', unit_of_measure: '', total_quantity: 1,
    reorder_threshold: '', home_location_id: '', notes: '',
    manufacturer_website: '', manufacturer_phone: ''
  });

  // Battery platform only makes sense for cordless gear, so the field is hidden
  // unless the chosen category is one of these. Matched on category code rather
  // than name so renaming a category in the database cannot break it.
  const BATTERY_CATEGORY_CODES = ['BATT', 'PWR'];

  const selectedCategory = categories.find(c => c.category_id === form.category_id);
  const showBatteryPlatform = BATTERY_CATEGORY_CODES.includes(selectedCategory?.code);

  useEffect(() => {
    getCategories().then(r => setCategories(r.data.categories || [])).catch(() => {});
    getLocations().then(r => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  // If the category moves away from battery-powered gear, drop any platform
  // already chosen. Without this the field hides while still holding a value,
  // and that value would be saved with no way to see or change it.
  useEffect(() => {
    if (!showBatteryPlatform && form.battery_platform) {
      setForm(f => ({ ...f, battery_platform: '' }));
    }
  }, [showBatteryPlatform, form.battery_platform]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

const handleSave = async () => {
    if (!form.name || !form.category_id) {
      setError('Name and category are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Clean empty strings to null — critical for UUID fields
      const cleaned = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      if (photoUrl) cleaned.photo_url = photoUrl;
      const res = await createItem(cleaned);
      setSavedItem(res.data.item);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save item';
      setError(msg);
      setSaving(false);
    }
  };

  const assetCategories = categories.filter(c => c.item_type === 'asset' || c.item_type === 'both');
  const bulkCategories  = categories.filter(c => c.item_type === 'bulk'  || c.item_type === 'both');

  return (
    <div className="screen">
      <div className="screen-header">
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Add New Item</h2>
      </div>

      <div className="screen-content">
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

        {/* Item Type toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {['asset', 'bulk'].map(type => (
            <button key={type} onClick={() => set('item_type', type)}
              className={`btn ${form.item_type === type ? 'btn-primary' : 'btn-secondary'}`}
              style={{ minHeight: 44 }}>
              {type === 'asset' ? '🔧 Serialized Asset' : '📦 Bulk / Consumable'}
            </button>
          ))}
        </div>

        <PhotoUpload currentUrl={photoUrl} onUpload={setPhotoUrl} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Item Name *">
            <input className="input" placeholder="e.g. Milwaukee M18 Drill" value={form.name}
              onChange={e => set('name', e.target.value)} />
          </Field>

          <Field label="Category *">
            <select className="input" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
              <option value="">Select category...</option>
              {(form.item_type === 'asset' ? assetCategories : bulkCategories).map(c => (
                <option key={c.category_id} value={c.category_id}>{c.name}</option>
              ))}
            </select>
          </Field>

          {form.item_type === 'asset' && (
            <>
              <Field label="Serial Number">
                <input className="input" placeholder="Manufacturer serial #" value={form.serial_number}
                  onChange={e => set('serial_number', e.target.value)} />
              </Field>
              <Field label="Model Number">
                <input className="input" placeholder="Model #" value={form.model_number}
                  onChange={e => set('model_number', e.target.value)} />
              </Field>
              {showBatteryPlatform && (
                <Field label="Battery Platform">
                  <select className="input" value={form.battery_platform} onChange={e => set('battery_platform', e.target.value)}>
                    <option value="">Not applicable</option>
                    <option>M18</option><option>M12</option>
                    <option>20V MAX</option><option>FLEXVOLT</option>
                    <option>18V</option><option>12V</option>
                  </select>
                </Field>
              )}
              <Field label="Purchase Price">
                <input className="input" type="number" placeholder="0.00" value={form.purchase_price}
                  onChange={e => set('purchase_price', e.target.value)} />
              </Field>
              <Field label="Purchase Date">
                <input className="input" type="date" value={form.purchase_date}
                  onChange={e => set('purchase_date', e.target.value)} />
              </Field>
              <Field label="Condition">
                <select className="input" value={form.condition} onChange={e => set('condition', e.target.value)}>
                  <option value="">Not specified</option>
                  <option value="new">New</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="repair">Needs Repair</option>
                  <option value="retired">Retired</option>
                </select>
              </Field>
            </>
          )}

          {form.item_type === 'bulk' && (
            <>
              <Field label="Unit of Measure">
                <select className="input" value={form.unit_of_measure} onChange={e => set('unit_of_measure', e.target.value)}>
                  <option value="">Select unit...</option>
                  {['sheet','box','roll','each','lb','lf','sf','gallon','tube','bag','bundle'].map(u => (
                    <option key={u}>{u}</option>
                  ))}
                </select>
              </Field>
              <Field label="Starting Quantity">
                <input className="input" type="number" min="0" value={form.total_quantity}
                  onChange={e => set('total_quantity', e.target.value)} />
              </Field>
              <Field label="Reorder Alert Threshold">
                <input className="input" type="number" min="0" placeholder="Alert when stock falls below..."
                  value={form.reorder_threshold} onChange={e => set('reorder_threshold', e.target.value)} />
              </Field>
            </>
          )}

          <Field label="Home Location">
            <select className="input" value={form.home_location_id} onChange={e => set('home_location_id', e.target.value)}>
              <option value="">Select location...</option>
              {locations.map(l => (
                <option key={l.location_id} value={l.location_id}>{l.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Manufacturer Website">
            <input className="input" placeholder="https://www.manufacturer.com" value={form.manufacturer_website || ''}
              onChange={e => set('manufacturer_website', e.target.value)} />
          </Field>

          <Field label="Manufacturer Phone">
            <input className="input" placeholder="1-800-xxx-xxxx" value={form.manufacturer_phone || ''}
              onChange={e => set('manufacturer_phone', e.target.value)} />
          </Field>

          <Field label="Notes">
            <textarea className="input" rows={3} placeholder="Any additional notes..."
              value={form.notes} onChange={e => set('notes', e.target.value)}
              style={{ resize: 'none' }} />
          </Field>
        </div>

        <button className="btn btn-primary" style={{ marginTop: 24 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : '✅ Save Item & Generate QR Label'}
        </button>
        <button className="btn btn-ghost" style={{ marginTop: 10 }} onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>

      {savedItem && (
        <LabelPrintModal
          item={savedItem}
          autoMode
          onClose={() => navigate(`/item/${savedItem.item_id}`)}
        />
      )}
    </div>
  );
}